import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, RangeSlider, RangeSliderValue, SimpleGrid, Stack } from '@mantine/core';
import dayjs from 'dayjs';
import { useMask, format } from '@react-input/mask';
import { TimeInput } from '@mantine/dates';

import { CreditRequestStep, CreditResponse, MaritalStatus, ValidationRules } from 'types';
import { Button, CheckBox, DatePicker, InputLabel, MonthPicker, SectionLabel, Select, Text, Textarea, TextInput, YesNoRadioButton } from 'components';
import { AppConstants } from '../../../../constants';
import CreditRequestPersonalChildren from './personal/children';
import { AppUtils } from 'utils';

const CITY_DATA = require('../../../../assets/data/cityData.json');
const POSTAL_DATA = require('../../../../assets/data/postalData.json');
let timeout: any;

const { CREDIT_REQUEST_FORM_PADDING, MASK_SWISS_PHONE_NUMBER, PHONE_NUMBER_CODE_SWITZERLAND } = AppConstants;

function convertTimeToNumber(timeValue: string | undefined, defaultValue: number) {
  return timeValue ? Number(timeValue.split(':')[0]) : defaultValue;
}

const PersonalInformationStep = ({
  form,
  isEditable,
  onSubmit,
  options,
  setValues,
  t,
}: CreditRequestStep) => {
  const [shouldSendCity, setShouldSendCity] = useState<boolean>(false);
  const [shouldSendPostal, setShouldSendPostal] = useState<boolean>(false);
  const [partnerFullPhoneNumber, setPartnerFullPhoneNumber] = useState<string | undefined>((form.values.partnerCountryCode && form.values.partnerPhoneNumber) ? (form.values.partnerCountryCode + ' ' + form.values.partnerPhoneNumber) : undefined);
  const [partnerPhoneNumberError, setPartnerPhoneNumberError] = useState<string>('');

  const initialPhoneValue = format(
    form.values.user.phoneNumber,
    {
      mask: MASK_SWISS_PHONE_NUMBER,
      replacement: { _: /\d/ }
    }
  );
  
  const userPhoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  useEffect(() => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
    timeout = setTimeout(() => {
      form.setFieldValue('partnerPhoneNumber', partnerFullPhoneNumber?.replace(PHONE_NUMBER_CODE_SWITZERLAND, '').trim());
    }, 500);
  }, [partnerFullPhoneNumber]);

  useEffect(() => {
    if (form.values.postal) {
      checkCity(form.values.postal, 'city');
    }
  }, [form.values.postal]);

  useEffect(() => {
    if (form.values.city) {
      checkPostal(form.values.city, 'postal');
    }
  }, [form.values.city]);

  useEffect(() => {
    if (form.values.previousPostal) {
      checkCity(form.values.previousPostal, 'previousCity');
    }
  }, [form.values.previousPostal]);

  useEffect(() => {
    if (form.values.previousCity) {
      checkPostal(form.values.previousCity, 'previousPostal');
    }
  }, [form.values.previousCity]);

  function checkCity(postalValue: string, updateProperty: keyof CreditResponse) {
    if (!shouldSendCity || AppUtils.isEmpty(postalValue) || postalValue.length < 4) {
      return;
    }
    const city = (POSTAL_DATA as Record<string, string>)[postalValue] || undefined;
    if (city) {
      setTimeout(() => {
        setShouldSendPostal(false);
        form.setFieldValue(updateProperty, city);
      }, 500);
    }
  }

  function checkPostal(cityValue: string, updateProperty: keyof CreditResponse) {
    if (!shouldSendPostal || AppUtils.isEmpty(cityValue)) {
      return;
    }
    const postal = (CITY_DATA as Record<string, string>)[cityValue] || undefined;
    if (postal) {
      setTimeout(() => {
        setShouldSendCity(false);
        form.setFieldValue(updateProperty, postal);
      }, 500);
    }
  }

  const onSubmitWrapper = useCallback(() => {
    if (isEditable && !isFormValid()) {
      return;
    }
    onSubmit();
  }, [form.values, options, isEditable]);

  const isFormValid = useCallback(() => {
    let isValid = true;
    setPartnerPhoneNumberError('');

    const runValidation = (name: keyof CreditResponse, value: any, rules: ValidationRules) => {
      const result = AppUtils.validateField(name, value, rules, form, t);
      if (!result) {
        isValid = false;
      }
    };

    runValidation('gender', form.values.gender, { required: true });
    runValidation('birthDate', form.values.birthDate, { required: true });
    runValidation('workdayAvailabilityFrom', form.values.workdayAvailabilityFrom, { required: true });
    runValidation('workdayAvailabilityTo', form.values.workdayAvailabilityTo, { required: true });
    if (form.values.saturdayAvailability) {
      runValidation('saturdayAvailabilityFrom', form.values.saturdayAvailabilityFrom, { required: true });
    runValidation('saturdayAvailabilityTo', form.values.saturdayAvailabilityTo, { required: true });
    }
    runValidation('nationality', form.values.nationality, { required: true });
    if (form.values.nationality && form.values.nationality !== 'SWITZERLAND' && form.values.nationality !== 'LIECHTENSTEIN') {
      runValidation('visaType', form.values.visaType, { required: true });
      if (form.values.visaType === 'B') {
        runValidation('visaFrom', form.values.visaFrom, { required: true });
      }
    }

    runValidation('postal', form.values.postal, { required: true });
    runValidation('city', form.values.city, { required: true });
    runValidation('street', form.values.street, { required: true });
    runValidation('houseNumber', form.values.houseNumber, { required: true });
    if (form.values.houseNumber && !/^[0-9]/.test(form.values.houseNumber.charAt(0))) {
      isValid = false;
      form.setFieldError('houseNumber', t('input.houseNumber.startWith1'));
    }
    runValidation('livingFrom', form.values.livingFrom, { required: true });
    if (form.values.livingFrom && AppUtils.isYoungerThanTwelveMonths(form.values.livingFrom)) {
      runValidation('previousPostal', form.values.previousPostal, { required: true });
      runValidation('previousCity', form.values.previousCity, { required: true });
      runValidation('previousStreet', form.values.previousStreet, { required: true });
      runValidation('previousHouseNumber', form.values.previousHouseNumber, { required: true });
      if (form.values.previousHouseNumber && !/^[0-9]/.test(form.values.previousHouseNumber.charAt(0))) {
        isValid = false;
        form.setFieldError('previousHouseNumber', t('input.houseNumber.startWith1'));
      }
    }

    runValidation('maritalStatus', form.values.maritalStatus, { required: true });
    if (form.values.maritalStatus === 'MARRIED' && form.values.partnerOnSameAddress && form.values.partnerIncludeInApplication) {
      runValidation('partnerBirthDate', form.values.partnerBirthDate, { required: true });
      runValidation('partnerNationality', form.values.partnerNationality, { required: true });
      if (form.values.partnerEmail && form.values.user.email === form.values.partnerEmail) {
        isValid = false;
        form.setFieldError('partnerEmail', t('input.email.sameAsUser'));
      }
      if (form.values.partnerPhoneNumber) {
        const partnerPhoneNumber = form.values.partnerPhoneNumber.replaceAll(' ', '');
        const userPhoneNumber = form.values.user.phoneNumber.replaceAll(' ', '');
        if (partnerPhoneNumber === userPhoneNumber) {
          isValid = false;
          setPartnerPhoneNumberError(t('input.phoneNumber.sameAsUser'));
          form.setFieldError('partnerPhoneNumber', t('input.phoneNumber.sameAsUser'));
        }
        if (form.values.partnerCountryCode?.startsWith(PHONE_NUMBER_CODE_SWITZERLAND) && !partnerPhoneNumber.startsWith('7') && partnerPhoneNumber.length > 0) {
          setPartnerPhoneNumberError(t('input.phoneNumber.swissStartWith'));
          form.setFieldError('partnerPhoneNumber', t('input.phoneNumber.swissStartWith'));
        }
      }
      if (form.values.partnerNationality && form.values.partnerNationality !== 'SWITZERLAND' && form.values.partnerNationality !== 'LIECHTENSTEIN') {
        runValidation('partnerVisaType', form.values.partnerVisaType, { required: true });
        if (form.values.partnerVisaType === 'B') {
          runValidation('partnerVisaFrom', form.values.partnerVisaFrom, { required: true });
        }
      }
    }
    return isValid;
  }, [form.values, options]);


  const updateTime = useCallback((fieldNames: string[], value: RangeSliderValue) => {
    value.forEach((val, index) => {
      const timeValue = [
        val.toString().padStart(2, '0'),
        '00'
      ].join(':');
      form.setFieldValue(fieldNames[index], timeValue);
    });
  }, [form]);

  const nationalities = useMemo(() => AppUtils.getOrderedCountryList(options['nationality'], t), [options, t])

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING}>
        <TextInput
          label={t('input.fullName.label')}
          defaultValue={form.values.user.fullName}
          disabled={true}
        />
        <Select
          label={t('input.gender.label')}
          data={['MALE', 'FEMALE', 'OTHER'].map(el => (
            { value: el, label: t(`input.gender.options.${el}`) }
          ))}
          key={form.key('gender')}
          {...form.getInputProps('gender')}
          clearable={true}
          required={true}
          disabled={!isEditable}
        />
        <DatePicker
          label={t('input.birthDate.label')}
          placeholder={t('input.date.placeholder')}
          key={form.key('birthDate')}
          {...form.getInputProps('birthDate')}
          minDate={dayjs().subtract(65, 'years').toDate()}
          maxDate={dayjs().subtract(18, 'years').toDate()}
          required={true}
          disabled={!isEditable}
        />
        <TextInput
          label={t('input.email.label')}
          defaultValue={form.values.user.email}
          disabled={true}
        />
        <TextInput
          label={t('input.phoneNumber.label')}
          defaultValue={initialPhoneValue}
          disabled={true}
          ref={userPhoneNumberRef}
        />
        <Box>
          <InputLabel label={t('input.workdayAvailability.label')} />
          <Flex align={'center'} justify={'space-between'}>
            <TimeInput
              key={form.key('workdayAvailabilityFrom')}
              {...form.getInputProps('workdayAvailabilityFrom')}
              min={options['workdayAvailability']?.min}
              max={options['workdayAvailability']?.max}
              disabled={!isEditable}
              w={'40%'}
              styles={{ input: { textAlign: 'center' } }}
            />
            <TimeInput
              key={form.key('workdayAvailabilityTo')}
              {...form.getInputProps('workdayAvailabilityTo')}
              min={options['workdayAvailability']?.min}
              max={options['workdayAvailability']?.max}
              disabled={!isEditable}
              w={'40%'}
              styles={{ input: { textAlign: 'center' } }}
            />
          </Flex>
          <RangeSlider
            minRange={1}
            min={options['workdayAvailability']?.min}
            max={options['workdayAvailability']?.max}
            step={1}
            defaultValue={[
              convertTimeToNumber(form.values.workdayAvailabilityFrom, options['workdayAvailability']?.min),
              convertTimeToNumber(form.values.workdayAvailabilityTo, options['workdayAvailability']?.max),
            ]}
            value={[
              convertTimeToNumber(form.values.workdayAvailabilityFrom, options['workdayAvailability']?.min),
              convertTimeToNumber(form.values.workdayAvailabilityTo, options['workdayAvailability']?.max),
            ]}
            mt={10}
            label={null}
            color={'#378F0E'}
            styles={{ thumb: { backgroundColor: '#62AF45', border: 'none' } }}
            onChange={(value) => updateTime(['workdayAvailabilityFrom', 'workdayAvailabilityTo'], value)}
          />
        </Box>
        <Flex align={'center'}>
          <CheckBox
            key={form.key('saturdayAvailability')}
            {...form.getInputProps('saturdayAvailability')}
            checked={form.values.saturdayAvailability}
            disabled={!isEditable}
          />
          <Text size={'sm'} ml={8} c={!isEditable ? 'gray' : undefined}>{t('input.saturdayAvailability.label')}</Text>
        </Flex>
        {form.values.saturdayAvailability ?
          <Box>
            <InputLabel label={t('input.saturdayAvailability.label')} />
            <Flex align={'center'} justify={'space-between'}>
              <TimeInput
                key={form.key('saturdayAvailabilityFrom')}
                {...form.getInputProps('saturdayAvailabilityFrom')}
                min={options['saturdayAvailability']?.min}
                max={options['saturdayAvailability']?.max}
                disabled={!isEditable}
                w={'40%'}
                styles={{ input: { textAlign: 'center' } }}
              />
              <TimeInput
                key={form.key('saturdayAvailabilityTo')}
                {...form.getInputProps('saturdayAvailabilityTo')}
                min={options['saturdayAvailability']?.min}
                max={options['saturdayAvailability']?.max}
                disabled={!isEditable}
                w={'40%'}
                styles={{ input: { textAlign: 'center' } }}
              />
            </Flex>
            <RangeSlider
              minRange={1}
              min={options['saturdayAvailability']?.min}
              max={options['saturdayAvailability']?.max}
              step={1}
              defaultValue={[
                convertTimeToNumber(form.values.saturdayAvailabilityFrom, options['saturdayAvailability']?.min),
                convertTimeToNumber(form.values.saturdayAvailabilityTo, options['saturdayAvailability']?.max),
              ]}
              value={[
                convertTimeToNumber(form.values.saturdayAvailabilityFrom, options['saturdayAvailability']?.min),
                convertTimeToNumber(form.values.saturdayAvailabilityTo, options['saturdayAvailability']?.max),
              ]}
              mt={10}
              label={null}
              color={'#378F0E'}
              styles={{ thumb: { backgroundColor: '#62AF45', border: 'none' } }}
              onChange={(value) => updateTime(['saturdayAvailabilityFrom', 'saturdayAvailabilityTo'], value)}
            />
          </Box>
          : undefined}
        <Select
          label={t('input.nationality.label')}
          data={nationalities}
          key={form.key('nationality')}
          {...form.getInputProps('nationality')}
          clearable={true}
          required={true}
          disabled={!isEditable}
        />
        {form.values.nationality && form.values.nationality !== 'SWITZERLAND' && form.values.nationality !== 'LIECHTENSTEIN' ?
          <>
            <Select
              label={t('input.visaType.label')}
              data={options['visaType'].map(el => (
                { value: el, label: t(`input.visaType.options.${el}`) }
              ))}
              key={form.key('visaType')}
              {...form.getInputProps('visaType')}
              clearable={true}
              required={true}
              disabled={!isEditable}
            />
            {form.values.visaType === 'B' ?
              <DatePicker
                label={t('input.visaFrom.label')}
                key={form.key('visaFrom')}
                {...form.getInputProps('visaFrom')}
                maxDate={new Date()}
                required={true}
                disabled={!isEditable}
              />
              : undefined
            }
          </>
          : undefined}
        <SectionLabel label={t('credit.new.address')} variant={'green'} />
        <Box>
          <Flex direction={'row'} justify={'space-between'}>
            <TextInput
              label={t('input.postal.label')}
              key={form.key('postal')}
              {...form.getInputProps('postal')}
              placeholder={t('input.postal.label')}
              disabled={!isEditable}
              required={true}
              maxLength={4}
              onChange={(event) => {
                setShouldSendPostal(true);
                setShouldSendCity(true);
                form.setFieldValue('postal', event.currentTarget.value);
              }}
            />
            <TextInput
              label={t('input.city.label')}
              key={form.key('city')}
              {...form.getInputProps('city')}
              placeholder={t('input.city.label')}
              disabled={!isEditable}
              required={true}
              onChange={(event) => {
                setShouldSendPostal(true);
                setShouldSendCity(true);
                form.setFieldValue('city', event.currentTarget.value);
              }}
            />
          </Flex>
        </Box>
        <Box>
          <Flex direction={'row'} justify={'space-between'}>
            <TextInput
              label={t('input.street.label')}
              key={form.key('street')}
              {...form.getInputProps('street')}
              placeholder={t('input.street.label')}
              disabled={!isEditable}
              required={true}
            />
            <TextInput
              label={t('input.houseNumber.label')}
              key={form.key('houseNumber')}
              {...form.getInputProps('houseNumber')}
              placeholder={t('input.houseNumber.label')}
              onBeforeInput={(event: any) => {
                const cursorPos = event.currentTarget.selectionStart || 0;
                if (cursorPos === 0) {
                  const inputValue = (event as any).data;
                  if (inputValue && !/^[0-9]/.test(inputValue)) {
                    event.preventDefault();
                  }
                }
              }}
              disabled={!isEditable}
              required={true}
            />
          </Flex>
        </Box>
        <MonthPicker
          label={t('input.livingFrom.label')}
          key={form.key('livingFrom')}
          {...form.getInputProps('livingFrom')}
          minDate={form.values.birthDate}
          maxDate={new Date()}
          required={true}
          disabled={!isEditable}
        />
        {form.values.livingFrom && AppUtils.isYoungerThanTwelveMonths(new Date(form.values.livingFrom)) ?
          <>
            <SectionLabel label={t('credit.new.previousAddress')} variant={'green'} />
            <Box>
              <Flex direction={'row'} justify={'space-between'}>
                <TextInput
                  label={t('input.postal.label')}
                  key={form.key('previousPostal')}
                  {...form.getInputProps('previousPostal')}
                  placeholder={t('input.postal.label')}
                  disabled={!isEditable}
                  required={true}
                  maxLength={4}
                  onChange={(event) => {
                    setShouldSendPostal(true);
                    setShouldSendCity(true);
                    form.setFieldValue('previousPostal', event.currentTarget.value);
                  }}
                />
                <TextInput
                  label={t('input.city.label')}
                  key={form.key('previousCity')}
                  {...form.getInputProps('previousCity')}
                  placeholder={t('input.city.label')}
                  disabled={!isEditable}
                  required={true}
                  onChange={(event) => {
                    setShouldSendPostal(true);
                    setShouldSendCity(true);
                    form.setFieldValue('previousCity', event.currentTarget.value);
                  }}
                />
              </Flex>
            </Box>
            <Box>
              <Flex direction={'row'} justify={'space-between'}>
                <TextInput
                  label={t('input.street.label')}
                  key={form.key('previousStreet')}
                  {...form.getInputProps('previousStreet')}
                  placeholder={t('input.street.label')}
                  disabled={!isEditable}
                  required={true}
                />
                <TextInput
                  label={t('input.houseNumber.label')}
                  key={form.key('previousHouseNumber')}
                  {...form.getInputProps('previousHouseNumber')}
                  placeholder={t('input.houseNumber.label')}
                  disabled={!isEditable}
                  onBeforeInput={(event: any) => {
                    const cursorPos = event.currentTarget.selectionStart || 0;
                    if (cursorPos === 0) {
                      const inputValue = (event as any).data;
                      if (inputValue && !/^[0-9]/.test(inputValue)) {
                        event.preventDefault();
                      }
                    }
                  }}
                  required={true}
                />
              </Flex>
            </Box>
          </>
          : undefined}
      </Stack>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING}>
        <CreditRequestPersonalChildren form={form} setValues={setValues} t={t} disabled={!isEditable} />
        <Select
          label={t('input.maritalStatus.label')}
          data={options['maritalStatus'].map(el => (
            { value: el, label: t(`input.maritalStatus.options.${el}`) }
          ))}
          key={form.key('maritalStatus')}
          {...form.getInputProps('maritalStatus')}
          onChange={(val) => {
            form.setFieldValue('maritalStatus', val as MaritalStatus);
            if (form.values.houseSituation === 'SPOUSE' && val === 'SINGLE') {
              form.setFieldValue('houseSituation', undefined);
            }
            if (val !== 'MARRIED') {
              form.setFieldValue('partnerOnSameAddress', undefined);
              form.setFieldValue('partnerIncludeInApplication', undefined);
              form.setFieldValue('partnerGender', undefined);
              form.setFieldValue('partnerFirstName', undefined);
              form.setFieldValue('partnerLastName', undefined);
              form.setFieldValue('partnerBirthDate', undefined);
              form.setFieldValue('partnerEmail', undefined);
              form.setFieldValue('partnerCountryCode', undefined);
              form.setFieldValue('partnerPhoneNumber', undefined);
              form.setFieldValue('partnerWorkdayAvailabilityFrom', undefined);
              form.setFieldValue('partnerWorkdayAvailabilityTo', undefined);
              form.setFieldValue('partnerSaturdayAvailability', undefined);
              form.setFieldValue('partnerSaturdayAvailabilityFrom', undefined);
              form.setFieldValue('partnerSaturdayAvailabilityTo', undefined);
              form.setFieldValue('partnerNationality', undefined);
              form.setFieldValue('partnerVisaType', undefined);
              form.setFieldValue('partnerVisaFrom', undefined);
            }
          }}
          clearable={true}
          disabled={!isEditable}
          required={true}
        />
        {form.values.maritalStatus === 'MARRIED' ?
          <>
            <YesNoRadioButton
              name={'partnerOnSameAddress'}
              label={t('input.partnerOnSameAddress.label')}
              t={t}
              form={form}
              disabled={!isEditable}
            />
            {form.values.partnerOnSameAddress ?
              <>
                <YesNoRadioButton
                  name={'partnerIncludeInApplication'}
                  label={t('input.partnerIncludeInApplication.label')}
                  t={t}
                  form={form}
                  disabled={!isEditable}
                />
                {form.values.partnerIncludeInApplication ?
                  <>
                    <SectionLabel label={t('credit.new.partner')} variant={'green'} />
                    <TextInput
                      label={t('input.partnerFirstName.label')}
                      key={form.key('partnerFirstName')}
                      {...form.getInputProps('partnerFirstName')}
                      disabled={!isEditable}
                    />
                    <TextInput
                      label={t('input.partnerLastName.label')}
                      key={form.key('partnerLastName')}
                      {...form.getInputProps('partnerLastName')}
                      disabled={!isEditable}
                    />
                    <Select
                      label={t('input.partnerGender.label')}
                      data={['MALE', 'FEMALE', 'OTHER'].map(el => (
                        { value: el, label: t(`input.gender.options.${el}`) }
                      ))}
                      key={form.key('partnerGender')}
                      {...form.getInputProps('partnerGender')}
                      clearable={true}
                      disabled={!isEditable}
                    />
                    <DatePicker
                      label={t('input.partnerBirthDate.label')}
                      placeholder={t('input.date.placeholder')}
                      key={form.key('partnerBirthDate')}
                      {...form.getInputProps('partnerBirthDate')}
                      minDate={dayjs().subtract(65, 'years').toDate()}
                      maxDate={dayjs().subtract(18, 'years').toDate()}
                      required={true}
                      disabled={!isEditable}
                    />
                    <TextInput
                      label={t('input.partnerEmail.label')}
                      placeholder={t('input.email.placeholder')}
                      key={form.key('partnerEmail')}
                      {...form.getInputProps('partnerEmail')}
                      disabled={!isEditable}
                    />
                    <TextInput
                      label={t('input.partnerPhoneNumber.label')}
                      placeholder={t('input.phoneNumber.placeholder')}
                      value={partnerFullPhoneNumber}
                      type={'tel'}
                      onBeforeInput={(event: any) => {
                        const cursorPos = event.currentTarget.selectionStart || 0;
                        if (cursorPos === 0) {
                          const inputValue = event.data;
                          if (inputValue && inputValue !== '7') {
                            event.preventDefault();
                          }
                        }
                      }}
                      onChange={(event: any) => setPartnerFullPhoneNumber(event.currentTarget.value)}
                      disabled={!isEditable}
                      ref={phoneNumberRef}
                      error={partnerPhoneNumberError}
                    />
                    <Box>
                      <InputLabel label={t('input.partnerWorkdayAvailability.label')} />
                      <Flex align={'center'} justify={'space-between'}>
                        <TimeInput
                          key={form.key('partnerWorkdayAvailabilityFrom')}
                          {...form.getInputProps('partnerWorkdayAvailabilityFrom')}
                          min={options['workdayAvailability']?.min}
                          max={options['workdayAvailability']?.max}
                          disabled={!isEditable}
                          w={'40%'}
                          styles={{ input: { textAlign: 'center' } }}
                        />
                        <TimeInput
                          key={form.key('partnerWorkdayAvailabilityTo')}
                          {...form.getInputProps('partnerWorkdayAvailabilityTo')}
                          min={options['workdayAvailability']?.min}
                          max={options['workdayAvailability']?.max}
                          disabled={!isEditable}
                          w={'40%'}
                          styles={{ input: { textAlign: 'center' } }}
                        />
                      </Flex>
                      <RangeSlider
                        minRange={1}
                        min={options['workdayAvailability']?.min}
                        max={options['workdayAvailability']?.max}
                        step={1}
                        defaultValue={[
                          convertTimeToNumber(form.values.partnerWorkdayAvailabilityFrom, options['workdayAvailability']?.min),
                          convertTimeToNumber(form.values.partnerWorkdayAvailabilityTo, options['workdayAvailability']?.max),
                        ]}
                        value={[
                          convertTimeToNumber(form.values.partnerWorkdayAvailabilityFrom, options['workdayAvailability']?.min),
                          convertTimeToNumber(form.values.partnerWorkdayAvailabilityTo, options['workdayAvailability']?.max),
                        ]}
                        mt={10}
                        label={null}
                        color={'#378F0E'}
                        styles={{ thumb: { backgroundColor: '#62AF45', border: 'none' } }}
                        onChange={(value) => updateTime(['partnerWorkdayAvailabilityFrom', 'partnerWorkdayAvailabilityTo'], value)}
                      />
                    </Box>
                    <Flex align={'center'}>
                      <CheckBox
                        key={form.key('partnerSaturdayAvailability')}
                        {...form.getInputProps('partnerSaturdayAvailability')}
                        checked={form.values.partnerSaturdayAvailability}
                        disabled={!isEditable}
                      />
                      <Text size={'sm'} ml={8} c={!isEditable ? 'gray' : undefined}>{t('input.partnerSaturdayAvailability.label')}</Text>
                    </Flex>
                    {form.values.partnerSaturdayAvailability ?
                      <Box>
                        <InputLabel label={t('input.partnerSaturdayAvailability.label')} />
                        <Flex align={'center'} justify={'space-between'}>
                          <TimeInput
                            key={form.key('partnerSaturdayAvailabilityFrom')}
                            {...form.getInputProps('partnerSaturdayAvailabilityFrom')}
                            min={options['saturdayAvailability']?.min}
                            max={options['saturdayAvailability']?.max}
                            disabled={!isEditable}
                            w={'40%'}
                            styles={{ input: { textAlign: 'center' } }}
                          />
                          <TimeInput
                            key={form.key('partnerSaturdayAvailabilityTo')}
                            {...form.getInputProps('partnerSaturdayAvailabilityTo')}
                            min={options['saturdayAvailability']?.min}
                            max={options['saturdayAvailability']?.max}
                            disabled={!isEditable}
                            w={'40%'}
                            styles={{ input: { textAlign: 'center' } }}
                          />
                        </Flex>
                        <RangeSlider
                          minRange={1}
                          min={options['saturdayAvailability']?.min}
                          max={options['saturdayAvailability']?.max}
                          step={1}
                          defaultValue={[
                            convertTimeToNumber(form.values.partnerSaturdayAvailabilityFrom, options['saturdayAvailability']?.min),
                            convertTimeToNumber(form.values.partnerSaturdayAvailabilityTo, options['saturdayAvailability']?.max),
                          ]}
                          value={[
                            convertTimeToNumber(form.values.partnerSaturdayAvailabilityFrom, options['saturdayAvailability']?.min),
                            convertTimeToNumber(form.values.partnerSaturdayAvailabilityTo, options['saturdayAvailability']?.max),
                          ]}
                          mt={10}
                          label={null}
                          color={'#378F0E'}
                          styles={{ thumb: { backgroundColor: '#62AF45', border: 'none' } }}
                          onChange={(value) => updateTime(['partnerSaturdayAvailabilityFrom', 'partnerSaturdayAvailabilityTo'], value)}
                        />
                      </Box>
                      : undefined}
                    <Select
                      label={t('input.partnerNationality.label')}
                      data={nationalities}
                      key={form.key('partnerNationality')}
                      {...form.getInputProps('partnerNationality')}
                      clearable={true}
                      required={true}
                      disabled={!isEditable}
                    />
                    {form.values.partnerNationality && form.values.partnerNationality !== 'SWITZERLAND' && form.values.partnerNationality !== 'LIECHTENSTEIN' ?
                      <>
                        <Select
                          label={t('input.partnerVisaType.label')}
                          data={options['visaType'].map(el => (
                            { value: el, label: t(`input.visaType.options.${el}`) }
                          ))}
                          key={form.key('partnerVisaType')}
                          {...form.getInputProps('partnerVisaType')}
                          clearable={true}
                          required={true}
                          disabled={!isEditable}
                        />
                        {form.values.partnerVisaType === 'B' ?
                          <DatePicker
                            label={t('input.partnerVisaFrom.label')}
                            key={form.key('partnerVisaFrom')}
                            {...form.getInputProps('partnerVisaFrom')}
                            maxDate={new Date()}
                            required={true}
                            disabled={!isEditable}
                          />
                        : undefined}
                      </>
                      : undefined}
                  </>
                  : undefined}
              </>
              : undefined}
          </>
          : undefined}
        <Textarea
          label={t('input.comment.label')}
          key={form.key('comment2')}
          {...form.getInputProps('comment2')}
          info={t('input.comment.info.message')}
          disabled={!isEditable}
        />
        <Button onClick={onSubmitWrapper}>
          {t('action.next')}
        </Button>
      </Stack>
    </SimpleGrid>
  )
}

export default PersonalInformationStep;